import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { searchPreviewRecords } from '../api/search.api'
import { searchQueryKeys } from '../api/queryKeys'
import type { SearchRecordPageResponse } from '../models'
import { RECORD_PAGE_SIZE, RECORD_PREFETCH_BATCHES } from '../logic/constants'
import { buildRecordRows } from '../logic/rows'
import type { Row } from '../logic/types'
import {
  SEARCH_PAGE_QUERY_OPTIONS,
  logFailure,
  retryFailedPage,
  useFetchPagesWhile,
} from './searchPaging'
import { useScopedState } from './useScopedState'

const NO_ROWS: Row[] = []

interface PreviewRecordPagesOptions {
  /** Whether the search box is empty; only the empty search pages its records. */
  enabled: boolean
  /** The search the pages belong to: a new one shows the response's sample again. */
  scope: string
  /** The record total before any page arrives. */
  total: number
}

/**
 * The empty search's Records "See more": expanding the section loads pages of
 * twenty from the first record, one after another, until RECORD_PREFETCH_BATCHES
 * pages wait beyond the requested count. Once a page arrives, the pages replace
 * the response's small sample.
 */
export function usePreviewRecordPages({ enabled, scope, total }: PreviewRecordPagesOptions) {
  const [target, setTarget] = useScopedState<number | undefined>(scope, undefined)
  const pagesQuery = useInfiniteQuery({
    queryKey: searchQueryKeys.previewRecordPages,
    queryFn: ({ pageParam }) =>
      searchPreviewRecords(pageParam.offset, RECORD_PAGE_SIZE, pageParam.total).catch(
        logFailure('Failed to load more preview records')
      ),
    initialPageParam: { offset: 0, total },
    getNextPageParam: (lastPage, pages) =>
      lastPage.has_more && countRecords(lastPage) > 0
        ? { offset: mergeRecordRows(pages).length, total: lastPage.total }
        : undefined,
    // Loaded only on request, below.
    enabled: false,
    ...SEARCH_PAGE_QUERY_OPTIONS,
  })

  const pages = pagesQuery.data?.pages
  const rows = useMemo(() => (pages ? mergeRecordRows(pages) : NO_ROWS), [pages])
  const lastPage = pages?.[pages.length - 1]
  const active = enabled && target !== undefined
  const hasMore = lastPage ? lastPage.has_more : total > 0
  useFetchPagesWhile(
    pagesQuery,
    active && hasMore && rows.length - target < RECORD_PAGE_SIZE * RECORD_PREFETCH_BATCHES
  )

  return {
    /** Paged rows once this search asked for them; empty until then. */
    rows: active ? rows : NO_ROWS,
    total: active ? lastPage?.total : undefined,
    /** Loads pages until RECORD_PREFETCH_BATCHES pages wait beyond `desiredVisible` rows. */
    loadAhead: (desiredVisible: number) => {
      setTarget(desiredVisible)
      retryFailedPage(pagesQuery)
    },
  }
}

function countRecords(page: SearchRecordPageResponse): number {
  return page.groups.reduce((count, group) => count + group.records.length, 0)
}

/** Every page's rows in order, each record once. */
function mergeRecordRows(pages: SearchRecordPageResponse[]): Row[] {
  const rows: Row[] = []
  const seen = new Set<string>()
  for (const page of pages) {
    const fresh = buildRecordRows(page.groups, '').filter((row) => !seen.has(row.key))
    fresh.forEach((row) => seen.add(row.key))
    rows.push(...fresh)
  }
  return rows
}
